import { ordersBasketApiConnector } from 'apiConnector/orders-basket';
import { quoteRequestApi } from 'apiConnector/quote/quote';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import {
  handleExecuteResponse,
  handleQuoteResponse,
} from 'epic/workspace/ordersBasket/orderOperations/common/orderOperations.helper';
import type { QuoteAck } from 'models/quote/QuoteAck';
import type { QuoteRequestPayload } from 'models/quote/QuoteRequestPayload';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  ordersBasketFormQuoteAccepted,
  ordersBasketFormQuoteTimeout,
  ordersBasketFormTradeAccepted,
  ordersBasketFormTradeTimeout,
} from 'state/orders-basket/actions';
import type { OrdersBasketQuoteResponse, OrdersBasketTradeResponse } from 'state/orders-basket/models';
import { getOrdersBasketFormIdByQuoteId, getOrdersBasketQuoteRequestPayload } from 'state/orders-basket/selectors';
import { ordersBasketFormQuoteFailedThunk, ordersBasketFormTradeFailedThunk } from 'state/orders-basket/thunks';
import type { EpicWithThunk } from 'state/state.common';
import type { HttpError } from 'utils/http.old/httpClient.model';
import { throwOnTimeout } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { ordersBasketExecutionResult, ordersBasketQuoteResult } from 'utils/streaming/messages';

export interface IOrdersBasketExecuteFormApi {
  priceForm(request: QuoteRequestPayload, ackTimeoutInMillis: number): Observable<QuoteAck>;
  executeQuote(quoteId: string, tradeAckTimeoutMs: number): Observable<void>;
}

export class OrdersBasketFormExecutionEpicFactory {
  public priceFormEpic(): EpicWithThunk {
    return (actions$, state$, { httpClient, frontLogger, getSgmeConfiguration }) =>
      actions$.pipe(
        ofType('ORDERS_BASKET_FORM_QUOTE_REQUESTED'),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
          const request = getOrdersBasketQuoteRequestPayload(state, action.formId);
          const timeoutMs = getSgmeConfiguration().quoteAckTimeoutMs;

          return request
            ? quoteRequestApi(httpClient, timeoutMs)
                .requestQuote(request, 'ordersBasket', 'Tradable')
                .pipe(
                  map(quoteAck => ordersBasketFormQuoteAccepted(action.formId, quoteAck)),
                  catchError((error: HttpError) => {
                    frontLogger.error(
                      'An error occurred inside ordersBasketFormExecutionEpicpriceFormEpic {@FrontError}',
                      error,
                    );
                    if (error.name === 'HttpTimeoutError') {
                      return of(ordersBasketFormQuoteTimeout(action.formId));
                    }
                    return of(ordersBasketFormQuoteFailedThunk(action.formId, apiErrorHelper.toApiErrorMessage(error)));
                  }),
                )
            : of(ordersBasketFormQuoteFailedThunk(action.formId));
        }),
      );
  }

  public quoteResultReceivedEpic(appScheduler?: SchedulerLike): EpicWithThunk {
    return (action$, _state$, { oldHttpClient, frontLogger, streaming }) =>
      action$.pipe(
        ofType('ORDERS_BASKET_FORM_QUOTE_ACCEPTED'),
        mergeMap(({ formId, quoteAck: { quoteId, pricingEstimatedDurationInMilliseconds: timeoutMs } }) => {
          const tradeAuthFailed$ = action$.pipe(
            ofType(
              'ORDERS_BASKET_FORM_TRADE_AUTH_FAILED',
              'ORDERS_BASKET_FORM_TRADE_AUTH_TIMEOUT',
              'ORDERS_BASKET_FORM_QUOTE_CANCELED',
            ),
            filter(action => action.formId === formId),
          );

          return ordersBasketQuoteResult(streaming).pipe(
            filter(event => event.quoteId === quoteId),
            mergeMap(lightEvent =>
              fatEventApiConnector(oldHttpClient)
                .getFatEvent<OrdersBasketQuoteResponse>(lightEvent)
                .pipe(
                  map(quoteResponse => handleQuoteResponse(quoteResponse)),
                  catchError((error: Error) =>
                    throwError(new apiErrorHelper.EpicApiError(apiErrorHelper.defaultTechnicalErrorKey, error)),
                  ),
                ),
            ),
            takeUntil(tradeAuthFailed$),
            take(1),
            throwOnTimeout(
              timeoutMs,
              new apiErrorHelper.TimeoutError(timeoutMs, 'quote.errorMessages.responseTimeout'),
              appScheduler,
            ),
            catchError((error: Error) => {
              if (error.name === 'TimeoutError') {
                return of(ordersBasketFormQuoteTimeout(formId));
              }
              frontLogger.error('An error occurred inside quoteResultReceivedEpic {@FrontError}', error);
              return of(ordersBasketFormQuoteFailedThunk(formId, apiErrorHelper.toApiErrorMessage(error)));
            }),
          );
        }),
      );
  }

  public executeFormEpic(): EpicWithThunk {
    return (actions$, _state$, { oldHttpClient, frontLogger, getSgmeConfiguration }) =>
      actions$.pipe(
        ofType('ORDERS_BASKET_FORM_TRADE_REQUESTED'),
        mergeMap(action => {
          const timeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;

          return ordersBasketApiConnector(oldHttpClient)
            .executeQuote(action.quoteId, timeoutMs, action.product)
            .pipe(
              map(() => ordersBasketFormTradeAccepted(action.formId, action.quoteId)),
              catchError((error: Error) => {
                frontLogger.error(
                  'An error occurred inside ordersBasketFormExecutionEpic.executeFormEpic {@FrontError}',
                  error,
                );
                if (error.name === 'HttpTimeoutError') {
                  return of(ordersBasketFormTradeTimeout(action.formId));
                }
                return of(
                  ordersBasketFormTradeFailedThunk(
                    action.formId,
                    apiErrorHelper.toApiErrorMessage(error, {
                      timeoutErrorMessage: 'trade.errorMessages.requestTimeout',
                    }),
                  ),
                );
              }),
            );
        }),
      );
  }

  public tradeResultReceivedEpic(appScheduler?: SchedulerLike): EpicWithThunk {
    return (actions$, state$, { oldHttpClient, getSgmeConfiguration, frontLogger, streaming }) =>
      actions$.pipe(
        ofType('ORDERS_BASKET_FORM_TRADE_ACCEPTED'),
        mergeMap(({ formId, quoteId }) => {
          const timeoutMs = getSgmeConfiguration().tradeExecReportTimeoutMs;

          return ordersBasketExecutionResult(streaming).pipe(
            filter(event => event.quoteId === quoteId),
            mergeMap(lightEvent =>
              fatEventApiConnector(oldHttpClient)
                .getFatEvent<OrdersBasketTradeResponse>(lightEvent)
                .pipe(catchError(_ => EMPTY)),
            ),
            withLatestFrom(state$),
            mergeMap(([tradeResponse, state]) => {
              const formId = getOrdersBasketFormIdByQuoteId(state, tradeResponse.quoteId);
              return formId ? of(handleExecuteResponse(formId, tradeResponse)) : EMPTY;
            }),
            take(1),
            throwOnTimeout(
              timeoutMs,
              new apiErrorHelper.TimeoutError(timeoutMs, 'trade.errorMessages.executionReportTimeout'),
              appScheduler,
            ),
            catchError((error: Error) => {
              frontLogger.error('An error occurred inside tradeResultReceivedEpic {@FrontError}', error);
              if (error.name === 'TimeoutError') {
                return of(ordersBasketFormTradeTimeout(formId));
              }
              return of(ordersBasketFormTradeFailedThunk(formId, apiErrorHelper.toApiErrorMessage(error)));
            }),
          );
        }),
      );
  }
}
export const ordersBasketFormExecutionEpic = new OrdersBasketFormExecutionEpicFactory();
